import ExcelJS from 'exceljs';
import { RECETA_OUTPUT_PRODUCT_TYPES, pepUsoEnPtLabel } from '../data/mockRecetasClassification';
import { resolveRecipeIngredientUnitDisplay } from './unitSelectUtils';

const HEADER_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFD1FAE5' },
};

export const RECETA_EXPORT_COL = {
  code: 'Código receta',
  name: 'Nombre receta',
  category: 'Categoría',
  subcategory: 'Subcategoría',
  family: 'Familia',
  active: 'Estado',
  outputType: 'Tipo producto salida',
  outputQuantity: 'Cantidad salida',
  outputUnit: 'Unidad salida',
  pepUsoEnPt: 'Uso en guía PT',
  sku: 'SKU insumo',
  productName: 'Insumo',
  quantity: 'Cantidad',
  unit: 'Unidad',
};

function downloadBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function styleHeaderRow(row, labels) {
  labels.forEach((label, i) => {
    const c = row.getCell(i + 1);
    c.value = label;
    c.font = { bold: true };
    c.fill = HEADER_FILL;
  });
}

function outputTypeLabel(value) {
  const meta = RECETA_OUTPUT_PRODUCT_TYPES.find((o) => o.value === value);
  return meta?.label || 'Producto terminado';
}

function formatQuantity(value) {
  if (value === '' || value == null) return '';
  const n = parseFloat(String(value).replace(',', '.'));
  if (!Number.isFinite(n)) return String(value);
  const s = n.toFixed(8).replace(/\.?0+$/, '');
  return s === '' ? '0' : s;
}

function formatExportDate() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
}

/**
 * Genera Excel con una fila por ingrediente de cada receta.
 * @param {{ recipes: object[], includeOutputConfig?: boolean, units?: object[], globalFabricarSubrecetaDefault?: boolean }} options
 */
export async function downloadRecetasProductsExcel({
  recipes = [],
  includeOutputConfig = false,
  units = [],
  globalFabricarSubrecetaDefault = false,
} = {}) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Recetas', { views: [{ state: 'frozen', ySplit: 2 }] });

  const headers = [
    RECETA_EXPORT_COL.code,
    RECETA_EXPORT_COL.name,
    RECETA_EXPORT_COL.category,
    RECETA_EXPORT_COL.subcategory,
    RECETA_EXPORT_COL.family,
    RECETA_EXPORT_COL.active,
    ...(includeOutputConfig
      ? [
          RECETA_EXPORT_COL.outputType,
          RECETA_EXPORT_COL.outputQuantity,
          RECETA_EXPORT_COL.outputUnit,
          RECETA_EXPORT_COL.pepUsoEnPt,
        ]
      : []),
    RECETA_EXPORT_COL.sku,
    RECETA_EXPORT_COL.productName,
    RECETA_EXPORT_COL.quantity,
    RECETA_EXPORT_COL.unit,
  ];

  const lastCol = String.fromCharCode('A'.charCodeAt(0) + headers.length - 1);
  ws.mergeCells(`A1:${lastCol}1`);
  ws.getCell('A1').value = includeOutputConfig
    ? 'Reporte de recetas con insumos. Una fila por ingrediente. Edite la columna «Uso en guía PT» en recetas PEP y vuelva a importar con «Importar Excel» para actualizar en masa. Valores: Subreceta automática | Guía PEP | — (sin cambio).'
    : 'Reporte de recetas con insumos. Una fila por ingrediente.';
  ws.getCell('A1').alignment = { wrapText: true, vertical: 'middle' };
  ws.getRow(1).height = 36;
  styleHeaderRow(ws.getRow(2), headers);

  const sorted = [...(recipes || [])].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' }),
  );

  for (const recipe of sorted) {
    const items = Array.isArray(recipe.items) && recipe.items.length ? recipe.items : [null];
    const base = [
      recipe.code || '',
      recipe.name || '',
      recipe.category_name || '',
      recipe.subcategory_name || '',
      recipe.family_name || '',
      recipe.is_active === false ? 'Inactiva' : 'Activa',
    ];
    if (includeOutputConfig) {
      base.push(
        outputTypeLabel(recipe.output_product_type),
        formatQuantity(recipe.output_quantity),
        recipe.output_unit_of_measure || '',
        pepUsoEnPtLabel(recipe, globalFabricarSubrecetaDefault),
      );
    }

    for (const item of items) {
      const sku = item?.product?.sku || '';
      const productName = item?.product?.name || '';
      const qty = item ? formatQuantity(item.quantity) : '';
      const unitRaw = item?.product?.unit_of_measure ?? item?.unit_of_measure ?? '';
      const unit = item ? resolveRecipeIngredientUnitDisplay(unitRaw, units) : '';
      ws.addRow([...base, sku, productName, qty, unit]);
    }
  }

  ws.columns = headers.map((h, i) => {
    if (h === RECETA_EXPORT_COL.name || h === RECETA_EXPORT_COL.productName) return { width: 32 };
    if (h === RECETA_EXPORT_COL.category || h === RECETA_EXPORT_COL.subcategory) return { width: 22 };
    if (i === 0) return { width: 14 };
    return { width: 16 };
  });

  const buf = await wb.xlsx.writeBuffer();
  downloadBuffer(buf, `reporte_recetas_productos_${formatExportDate()}.xlsx`);
}
